import {ContextTarget, useCreateDocumentContextMutation} from '@dms/api/core';
import {FileFieldConfig, FileFieldValue} from '@dms/api/vehicle';
import {handleApiError} from '@dms/shared';

import {ApiError, isDefined} from 'shared/web';

export type {FileFieldConfig, FileFieldValue} from '@dms/api/vehicle';

export interface SyncDocumentsParams {
  vehicleId: string;
  contextTarget: ContextTarget;
  fileFields: FileFieldConfig[];
}

export interface SyncResult {
  success: boolean;
  documentContextId?: string;
  error?: unknown;
}

/**
 * Hook to sync uploaded files with document-context system
 * Used in form submit handlers after vehicle is created/updated
 *
 * @example
 * const {syncDocuments} = useVehicleDocumentSync();
 *
 * const handleSubmit = async (values) => {
 *   const vehicle = await createVehicle(values).unwrap();
 *
 *   await syncDocuments({
 *     vehicleId: vehicle.id,
 *     contextTarget: 'vehicle',
 *     fileFields: [
 *       {
 *         fieldValue: values.batteryCertificateDocument,
 *         documentNote: 'Battery Certificate',
 *       },
 *     ],
 *   });
 * };
 */
export function useVehicleDocumentSync() {
  const [createDocumentContext] = useCreateDocumentContextMutation();

  const syncDocuments = async (params: SyncDocumentsParams): Promise<SyncResult[]> => {
    // Filter out null values (no file uploaded)
    const filesToSync = params.fileFields.filter(
      (field): field is FileFieldConfig & {fieldValue: FileFieldValue} =>
        isDefined(field.fieldValue)
    );

    // If no files to sync, return early
    if (filesToSync.length === 0) {
      return [];
    }

    // Create document-contexts for each file
    const results = await Promise.all(
      filesToSync.map((field) =>
        createDocumentContext({
          documentContextRequestBody: {
            fileRemoteId: field.fieldValue.fileId,
            contextTarget: params.contextTarget,
            contextId: params.vehicleId,
            fileName: field.fieldValue.fileName,
            note: field.documentNote,
          },
        })
          .unwrap()
          .then((response) => ({
            success: true as const,
            documentContextId: response.id,
          }))
          .catch((error) => {
            handleApiError(error as ApiError);
            return {
              success: false as const,
              error,
            };
          })
      )
    );

    return results;
  };

  return {syncDocuments};
}
